'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useApp } from '@/context/AppContext'
import { useLang } from '@/context/LangContext'
import { api } from '@/lib/api'
import { formatDT } from '@/lib/utils'
import { Plus, Search, Package, AlertTriangle, TrendingUp, ChevronRight, ArrowLeft, Upload } from 'lucide-react'
import Link from 'next/link'

export default function ArticlesPage() {
  const { session } = useApp()
  const { t } = useLang()
  const router = useRouter()

  const [articles, setArticles] = useState<any[]>([])
  const [filtered, setFiltered] = useState<any[]>([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!session) { router.replace('/login'); return }
    load()
  }, [session])

  useEffect(() => {
    if (!search) { setFiltered(articles); return }
    const q = search.toLowerCase()
    setFiltered(articles.filter(a => a.name.toLowerCase().includes(q) || a.barcode?.includes(q)))
  }, [search, articles])

  async function load() {
    const data = await api.getArticles(session!.shop.id)
    setArticles(data); setFiltered(data); setLoading(false)
  }

  const isPatron = session?.user.role === 'patron'

  function stockColor(a: any) {
    if (a.stock <= 0) return 'text-red-500 bg-red-50'
    if (a.stock < a.stock_alert) return 'text-amber-500 bg-amber-50'
    return 'text-green-600 bg-green-50'
  }

  return (
    <div className="min-h-screen pb-6">
      <div className="bg-white sticky top-0 z-10 shadow-sm">
        <div className="flex items-center gap-3 px-4 pt-12 pb-3">
          <Link href="/dashboard" className="p-2 -ml-2 text-gray-600"><ArrowLeft className="w-6 h-6" /></Link>
          <h1 className="text-xl font-bold text-gray-900 flex-1">Articles</h1>
          <div className="flex gap-2">
            {isPatron && (
              <Link href="/articles/import" className="p-2 text-gray-600"><Upload className="w-6 h-6" /></Link>
            )}
            <Link href="/articles/new" className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
              <Plus className="w-5 h-5 text-white" />
            </Link>
          </div>
        </div>
        <div className="px-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Chercher article ou barcode..."
              className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-2xl border border-gray-200 focus:outline-none focus:border-indigo-400 focus:bg-white" />
          </div>
        </div>
        <div className="flex gap-4 px-4 pb-3">
          <span className="text-sm text-gray-500"><span className="font-bold text-gray-800">{articles.length}</span> articles</span>
          <span className="text-sm text-amber-500 font-medium">
            <AlertTriangle className="w-4 h-4 inline mr-1" />
            {articles.filter(a => a.stock < a.stock_alert).length} alertes
          </span>
        </div>
      </div>

      <div className="px-4 pt-3 space-y-2">
        {loading ? (
          <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-12 h-12 mx-auto text-gray-300 mb-2" />
            <p className="text-gray-500">Aucun article</p>
            {articles.length === 0 && (
              <Link href="/articles/new" className="text-indigo-600 font-semibold mt-2 inline-block">Ajouter →</Link>
            )}
          </div>
        ) : filtered.map(a => (
          <Link key={a.id} href={`/articles/${a.id}`}
            className="bg-white rounded-2xl p-4 flex items-center gap-3 shadow-sm border border-gray-100 active:scale-98 transition-all">
            <div className="w-2 h-12 rounded-full flex-shrink-0" style={{ backgroundColor: a.category_color || '#e5e7eb' }} />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-800 truncate">{a.name}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${stockColor(a)}`}>
                  {a.stock <= 0 ? 'Épuisé' : `${a.stock} ${a.unit}`}
                </span>
                {a.category_name && <span className="text-xs text-gray-400">{a.category_name}</span>}
              </div>
              {isPatron && (
                <div className="flex items-center gap-1 mt-1">
                  <TrendingUp className="w-3 h-3 text-green-500" />
                  <span className="text-xs text-green-600 font-medium">
                    +{a.ras_mel > 0 ? (((a.prix_vente - a.ras_mel) / a.ras_mel) * 100).toFixed(0) : 0}% marge
                  </span>
                </div>
              )}
            </div>
            <div className="text-right flex-shrink-0">
              <p className="font-bold text-gray-900">{formatDT(a.prix_vente)}</p>
              {isPatron && <p className="text-xs text-gray-400">RM: {formatDT(a.ras_mel)}</p>}
            </div>
            <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
          </Link>
        ))}
      </div>
    </div>
  )
}
